import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DocumentService, DocumentInfo, UserConfigService } from '../../utils/apiService';
import Notification, { NotificationType } from '../Notification';
import DocumentPreview from './DocumentPreview';
import { Link } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';

// Utility function to get appropriate icon based on file MIME type
const getFileIcon = (mimeType?: string): React.ReactNode => {
  if (!mimeType) {
    // Default document icon
    return (
      <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    );
  }

  if (mimeType.startsWith('application/pdf')) {
    return (
      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    );
  }

  if (mimeType.startsWith('image/')) {
    return (
      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
      </svg>
    );
  }

  if (mimeType.startsWith('application/vnd.google-apps.document')) {
    return (
      <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    );
  }

  if (mimeType.startsWith('application/vnd.google-apps.spreadsheet')) {
    return (
      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
          d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
      </svg>
    );
  }

  // Default document icon for other types
  return (
    <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
};

interface DocumentListProps {
  // No props needed for now
}

interface NotificationState {
  type: NotificationType;
  message: string;
  isVisible: boolean;
}

interface DocumentPreviewState {
  isOpen: boolean;
  fileId: string;
  fileName: string;
  contentType: string | undefined;
}

const DocumentList: React.FC<DocumentListProps> = () => {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [notification, setNotification] = useState<NotificationState>({
    type: 'info',
    message: '',
    isVisible: false
  });
  const initialFetchMade = useRef(false);
  const lastFetchTimestamp = useRef(0);
  
  // Add a state to track if the error is related to configuration
  const [isConfigError, setIsConfigError] = useState(false);
  
  // New state for document preview
  const [documentPreview, setDocumentPreview] = useState<DocumentPreviewState>({
    isOpen: false,
    fileId: '',
    fileName: '',
    contentType: undefined
  });
  
  // Get configuration context
  const { configurations } = useConfig();
  
  // Check if the configuration actually contains valid document source settings
  const checkConfigurationValidity = async (): Promise<boolean> => {
    try {
      console.log("DocumentList: Checking configuration validity...");
      
      // Try to fetch user configurations 
      const configurations = await UserConfigService.getConfigurations();
      console.log("DocumentList: Configurations received:", configurations);
      
      // More aggressive check: Consider it invalid if configurations array is empty or undefined
      if (!configurations || !Array.isArray(configurations) || configurations.length === 0) {
        console.log("DocumentList: No configurations found or invalid configuration data");
        return false;
      }
      
      // Check if there's at least one configuration with a valid folder_id
      const validConfigs = configurations.filter(config => 
        config && 
        config.folder_id && 
        config.folder_id.trim() !== ""
      );
      
      console.log("DocumentList: Valid configurations found:", validConfigs.length);
      return validConfigs.length > 0;
    } catch (error) {
      console.error("DocumentList: Error checking configuration validity:", error);
      return false;
    }
  };
  
  const fetchDocuments = async (forceRefresh: boolean = false) => {
    if (loading) return; // Prevent concurrent fetches
    
    // Check if we've already determined there are no configurations
    // Skip the fetch altogether if we know configurations are missing
    const noConfigsDetected = sessionStorage.getItem('kms_no_configurations_detected') === 'true';
    if (noConfigsDetected && !forceRefresh) {
      console.log("DocumentList: Previously determined no configurations exist, skipping fetch");
      setIsConfigError(true);
      setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
      return;
    }
    
    // Implement throttling (avoid fetching more than once every 2 seconds) unless forceRefresh is true
    const now = Date.now();
    if (!forceRefresh && (now - lastFetchTimestamp.current < 2000)) {
      // Skip this fetch if it's too soon after the last one
      console.debug('Skipping fetch - too soon after previous fetch');
      return;
    }
    
    setLoading(true);
    setError(null);
    setIsConfigError(false);
    lastFetchTimestamp.current = now;
    
    try {
      // First check if we have a valid configuration
      console.log("fetchDocuments: Checking configuration validity before fetching documents");
      const hasValidConfig = await checkConfigurationValidity();
      
      if (!hasValidConfig) {
        // If we don't have a valid configuration, set the appropriate error
        console.log("fetchDocuments: No valid configuration found, showing configuration error message");
        setIsConfigError(true);
        setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
        setLoading(false);
        // Store in session to prevent repeated fetches
        sessionStorage.setItem('kms_no_configurations_detected', 'true');
        return;
      }
      
      // Double-check by directly fetching configurations
      try {
        const configurations = await UserConfigService.getConfigurations();
        console.log("fetchDocuments: Direct configurations check:", configurations);
        
        if (!configurations || !Array.isArray(configurations) || configurations.length === 0) {
          console.log("fetchDocuments: Direct check confirms no valid configurations");
          setIsConfigError(true);
          setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
          setLoading(false);
          // Store in session to prevent repeated fetches
          sessionStorage.setItem('kms_no_configurations_detected', 'true');
          return;
        }
        
        // Check specifically for configurations with folder IDs
        const validConfigs = configurations.filter(c => 
          c && c.folder_id && c.folder_id.trim() !== ""
        );
        
        if (validConfigs.length === 0) {
          console.log("fetchDocuments: No active configurations with valid folder IDs found");
          setIsConfigError(true);
          setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
          setLoading(false);
          // Store in session to prevent repeated fetches
          sessionStorage.setItem('kms_no_configurations_detected', 'true');
          return;
        }
        
        // If we got here, clear the no-configs flag
        sessionStorage.removeItem('kms_no_configurations_detected');
      } catch (error) {
        console.error("fetchDocuments: Error in direct configurations check:", error);
        // Continue anyway to see if document fetching works
      }
      
      // If we have a valid configuration, proceed with fetching documents
      console.log("fetchDocuments: Valid configuration found, proceeding to fetch documents");
      const response = await DocumentService.listDocuments(forceRefresh);
      setDocuments(response.documents);
      initialFetchMade.current = true;
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching documents', err);
      
      // Determine the type of error from the response
      const errorDetails = err.response?.data?.detail || err.message || 'Errore sconosciuto';
      const statusCode = err.response?.status || err.status;
      
      console.log("fetchDocuments error details:", { errorDetails, statusCode });
      
      // Check specifically for the "No active configuration" message which is a setup step, not an error
      const isConfigurationMessage = 
        errorDetails.includes("No active configuration") || 
        errorDetails.includes("Please set up a database") ||
        errorDetails.includes("configurazione") ||
        errorDetails.includes("folder ID") ||
        err.isConfigurationError;
      
      // Handle different error scenarios with tailored messages
      if (statusCode === 401 || statusCode === 403) {
        // Authentication error
        setError('La tua sessione è scaduta o non sei autorizzato ad accedere a questi documenti. ' +
                'Effettua nuovamente il login per continuare.');
        
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
      else if (isConfigurationMessage || statusCode === 404) {
        // This is not a true error but an expected state when no configuration exists
        console.log("fetchDocuments: Configuration-related error detected");
        setIsConfigError(true);
        setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
      }
      else if (statusCode === 500) {
        // Special handling for empty configuration file
        if (errorDetails.includes("Failed to retrieve configurations")) {
          console.log("fetchDocuments: Failed to retrieve configurations (500 error)");
          setIsConfigError(true);
          setError('È necessario creare la tua prima configurazione per accedere ai documenti. Vai alla pagina Configurazioni per iniziare.');
        }
        // Other server errors with more detailed information
        else if (errorDetails.includes("Google Drive") || errorDetails.includes("credentials")) {
          setError('Errore durante l\'accesso ai documenti di Google Drive. Potrebbe essere necessario ' +
                  'effettuare nuovamente il login o verificare la configurazione del database.');
        } else {
          setError('Si è verificato un errore interno del server. I dettagli tecnici sono: ' + errorDetails);
        }
      }
      else {
        // Default to configuration error for document-related 404s
        if (statusCode === 404 && errorDetails.includes("documents")) {
          console.log("fetchDocuments: Document endpoint 404 error, treating as configuration issue");
          setIsConfigError(true);
          setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
        } else {
          // Generic error message
          setError('Impossibile caricare i documenti: ' + errorDetails);
        }
      }
      
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialFetchMade.current) {
      fetchDocuments();
    }
  }, []);

  const hideNotification = () => {
    setNotification(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    else return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  // Function to check if a document is in the current folder
  const isDocumentInCurrentFolder = (doc: DocumentInfo): boolean => {
    if (!doc.file_path) return currentFolder === '';
    
    if (currentFolder === '') {
      // At root level, show only files that are directly in the root
      // (no folders in their path)
      const pathParts = doc.file_path.split(' / ');
      return pathParts.length === 1;
    }
    
    // For files in folders, check if they're in the exact current folder
    const folderPrefix = currentFolder + ' / ';
    if (!doc.file_path.startsWith(folderPrefix)) return false;
    
    // Make sure there are no additional subfolders
    const remainingPath = doc.file_path.substring(folderPrefix.length);
    return !remainingPath.includes(' / ');
  };

  // Helper function to extract folder structure from file paths
  const extractFolderStructure = useMemo(() => {
    const folderMap: { [key: string]: Set<string> } = { '': new Set<string>() };
    
    // First identify all folders from document paths
    documents.forEach(doc => {
      if (!doc.file_path) return;
      
      const pathParts = doc.file_path.split(' / ');
      if (pathParts.length <= 1) return; // Skip files with no folder
      
      // Extract all folder paths (excluding the last part which is the filename)
      let path = '';
      for (let i = 0; i < pathParts.length - 1; i++) {
        const folderName = pathParts[i];
        const previousPath = path;
        
        // Build the current path
        path = path ? `${path} / ${folderName}` : folderName;
        
        // Initialize folder in map if it doesn't exist
        if (!folderMap[path]) {
          folderMap[path] = new Set<string>();
        }
        
        // Add this folder to its parent's subfolders
        if (i === 0) {
          // Top-level folder goes into the root
          folderMap[''].add(path);
        } else if (previousPath) {
          // Add this folder to its parent
          folderMap[previousPath].add(path);
        }
      }
    });
    

    
    return folderMap;
  }, [documents]);
  
  // Force an initial fetch of documents when the component mounts
  useEffect(() => {
    // Force refresh documents on initial load to ensure we have the latest data
    fetchDocuments(true);
    
    // Add a special check for document 404 errors that might not be caught otherwise
    const checkNetworkFor404 = () => {
      setTimeout(() => {
        console.log("Checking for document 404 errors in network requests...");
        
        // Check if we already have a configuration error
        if (!isConfigError && !error) {
          // Look for document 404 errors in the network tab
          const has404 = document.querySelector('.status-code-404');
          const hasDocumentEndpoint = document.querySelector('.name-documents') || 
                                     document.querySelector('[title*="documents"]');
          
          if (has404 && hasDocumentEndpoint) {
            console.log("Found document 404 errors in network tab, forcing configuration error");
            setIsConfigError(true);
            setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
          }
        }
      }, 1000); // Wait 1 second to allow network requests to appear in the tab
    };
    
    checkNetworkFor404();
  }, []);

  // Function to navigate to a folder
  const navigateToFolder = (folderPath: string) => {
    setCurrentFolder(folderPath);
    // Clear search when navigating
    setSearchTerm('');
  };
  
  // Function to navigate up one level
  const navigateUp = () => {
    if (!currentFolder) return;
    
    const lastSlashIndex = currentFolder.lastIndexOf(' / ');
    if (lastSlashIndex === -1) {
      // At top level folder, go to root
      setCurrentFolder('');
    } else {
      // Go up one level
      setCurrentFolder(currentFolder.substring(0, lastSlashIndex));
    }
  };

  // Filter documents based on search term
  const searchFilteredDocuments = useMemo(() => {
    if (!searchTerm) return documents;
    return documents.filter(doc => 
      doc.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.file_path && doc.file_path.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [documents, searchTerm]);
  
  // Get documents and subfolders in current folder
  const currentFolderContents = useMemo(() => {
    if (searchTerm) {
      // When searching, don't filter by current folder
      return {
        documents: searchFilteredDocuments,
        subfolders: new Set<string>()
      };
    }
    
    // Get documents in current folder
    const docsInFolder = documents.filter(isDocumentInCurrentFolder);
    
    // Get subfolders in current folder
    const subfolders = extractFolderStructure[currentFolder] || new Set<string>();
    

    
    return {
      documents: docsInFolder,
      subfolders
    };
  }, [documents, currentFolder, searchTerm, extractFolderStructure, searchFilteredDocuments]);
  
  // Create breadcrumb navigation
  const breadcrumbs = useMemo(() => {
    if (!currentFolder) return [{ name: 'Home', path: '' }];
    
    const pathParts = currentFolder.split(' / ');
    const crumbs = [{ name: 'Home', path: '' }];
    
    let currentPath = '';
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      currentPath = currentPath ? `${currentPath} / ${part}` : part;
      crumbs.push({
        name: part,
        path: currentPath
      });
    }
    
    return crumbs;
  }, [currentFolder]);

  // Define sortedDocuments without sorting by folder
  const sortedDocuments = useMemo(() => {
    return currentFolderContents.documents;
  }, [currentFolderContents.documents]);

  // Function to handle preview opening
  const openDocumentPreview = (document: DocumentInfo) => {
    setDocumentPreview({
      isOpen: true,
      fileId: document.file_id,
      fileName: document.file_name,
      contentType: document.content_type
    });
  };

  // Function to close the preview
  const closeDocumentPreview = () => {
    setDocumentPreview({
      isOpen: false,
      fileId: '',
      fileName: '',
      contentType: undefined
    });
  };

  // Helper function to format file path for display
  const formatFilePath = (filePath: string | undefined): React.ReactNode => {
    if (!filePath) return <span className="italic">Percorso: N/A</span>;
    
    // Split the path by the separator
    const pathParts = filePath.split(" / ");
    
    // If there's only one part (just the filename), return it with a default root
    if (pathParts.length <= 1) {
      return <span className="italic">{filePath}</span>;
    }
    
    // Remove the last part as it's the filename
    const folders = pathParts.slice(0, -1);
    
    return (
      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
        <svg className="h-3.5 w-3.5 mr-1 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <span className="truncate">
          {folders.map((folder, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="mx-1 text-gray-400 dark:text-gray-500">/</span>}
              <span className="hover:text-blue-500 dark:hover:text-blue-400">{folder}</span>
            </React.Fragment>
          ))}
        </span>
      </div>
    );
  };

  // Helper to get folder name from a full folder path
  const getFolderName = (folderPath: string): string => {
    const lastSlashIndex = folderPath.lastIndexOf(' / ');
    if (lastSlashIndex === -1) return folderPath;
    return folderPath.substring(lastSlashIndex + 3);
  };

  // Folder component
  const FolderItem: React.FC<{ folderPath: string; onClick: () => void }> = ({ folderPath, onClick }) => {
    const folderName = getFolderName(folderPath);
    
    return (
      <div 
        className="flex items-center p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
        onClick={onClick}
      >
        <div className="flex items-center space-x-3 flex-1">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-yellow-500 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-md font-medium text-gray-900 dark:text-white">
              {folderName}
            </h3>
          </div>
        </div>
        <div className="text-gray-400 mr-2">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    );
  };

  // DocumentItem component for displaying a single document in the list
  const DocumentItem: React.FC<{
    document: DocumentInfo;
  }> = ({ document }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    const formatDate = (dateStr: string) => {
      try {
        const date = new Date(dateStr);
        return date.toLocaleString();
      } catch (e) {
        return 'Data non disponibile';
      }
    };
    
    return (
      <div className="bg-white dark:bg-gray-800 overflow-hidden transition-all duration-200">
        <div
          className="grid grid-cols-12 items-center p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* File name and icon - spans 7 columns */}
          <div className="col-span-7 flex items-center space-x-3 min-w-0">
            <div className="flex-shrink-0">
              {getFileIcon(document.content_type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="text-md font-medium text-gray-900 dark:text-white truncate">
                  {document.file_name}
                </h3>
                {configurations.length > 1 && document.source_folder_name && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    {document.source_folder_name}
                  </span>
                )}
              </div>
              {!currentFolder && document.file_path && (
                <div className="truncate">
                  {formatFilePath(document.file_path)}
                </div>
              )}
            </div>
          </div>
          
          {/* Date - spans 3 columns */}
          <div className="col-span-3 text-right">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatDate(document.last_modified || '')}
            </span>
          </div>
          
          {/* File size - spans 1 column */}
          <div className="col-span-1 text-right">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatFileSize(document.size)}
            </span>
          </div>
          
          {/* Actions - spans 1 column */}
          <div className="col-span-1 flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openDocumentPreview(document);
              }}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Anteprima documento"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-0 border-t border-gray-200 dark:border-gray-700">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="col-span-1">
                <dt className="text-gray-500 dark:text-gray-400">Dimensione</dt>
                <dd className="text-gray-900 dark:text-white mt-1">{formatFileSize(document.size)}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-gray-500 dark:text-gray-400">Ultima modifica</dt>
                <dd className="text-gray-900 dark:text-white mt-1">{formatDate(document.last_modified || '')}</dd>
              </div>
              {document.file_path && (
                <div className="col-span-2 mt-2">
                  <dt className="text-gray-500 dark:text-gray-400">Percorso completo</dt>
                  <dd className="text-gray-900 dark:text-white mt-1 break-all">{document.file_path}</dd>
                </div>
              )}
              <div className="col-span-2 mt-2">
                <dt className="text-gray-500 dark:text-gray-400">File ID</dt>
                <dd className="text-gray-900 dark:text-white mt-1 font-mono text-xs break-all">{document.file_id}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    );
  };

  // Breadcrumb Navigation Component
  const BreadcrumbNavigation: React.FC = () => {
    return (
      <nav className="flex mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2">
          {breadcrumbs.map((crumb, index) => (
            <li key={crumb.path} className="inline-flex items-center">
              {index > 0 && (
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              )}
              <button
                onClick={() => navigateToFolder(crumb.path)}
                className={`inline-flex items-center text-sm font-medium ${
                  index === breadcrumbs.length - 1
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'
                }`}
              >
                {index === 0 ? (
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                ) : null}
                {crumb.name}
              </button>
            </li>
          ))}
        </ol>
      </nav>
    );
  };

  // Add a new function to directly check for document 404 errors by looking at the DOM
  const checkForDocuments404 = () => {
    // Try to find 404 elements and document endpoints in the DOM
    const has404Elements = document.querySelectorAll('.status-code-404').length > 0;
    
    // Check for document endpoints in various ways
    const documentEndpointElements = 
      document.querySelectorAll('[title*="documents"]').length > 0 ||
      document.querySelectorAll('[title*="/api/v1/documents"]').length > 0 ||
      document.querySelectorAll('.name-documents').length > 0;
      
    // If we have both 404s and document endpoints, force the configuration error
    if (has404Elements && documentEndpointElements) {
      console.log("Found document 404 errors via DOM check, forcing configuration error message");
      // Force the configuration error state
      setIsConfigError(true);
      setError('Nessun documento caricato da Google Drive, vai in Configurazioni e aggiungi il tuo folder ID');
      return true;
    }
    
    return false;
  };
  
  // Run this check every time the component renders
  useEffect(() => {
    // Short delay to allow the DOM to update with network results
    const timeoutId = setTimeout(() => {
      checkForDocuments404();
    }, 500);
    
    return () => clearTimeout(timeoutId);
  });
  
  // Run this check before rendering anything
  const has404Error = checkForDocuments404();
  
  // If we directly detect a 404 error for documents, show the configuration message
  if (has404Error || (error && isConfigError)) {
    return (
      <div className="text-center py-16 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 max-w-2xl mx-auto">
        <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20">
          <svg 
            className="h-16 w-16 text-blue-500"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          </svg>
        </div>
        
        <h3 className="mt-4 text-lg font-medium text-blue-700 dark:text-blue-400">
          Configurazione Necessaria
        </h3>
        
        <p className="mt-2 text-md text-gray-600 dark:text-gray-400 max-w-md mx-auto">
          Nessun documento caricato da Google Drive. Vai in Configurazioni e aggiungi il tuo folder ID.
        </p>
        
        <div className="mt-6">
          <a
            href="/configurazioni"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Vai alla pagina Configurazioni
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    // Check if the error is related to missing configuration or authentication
    console.log("Error display - checking error type:", { error, isConfigError });
    
    // More aggressive checking for configuration-related errors
    const isConfigurationError = 
      isConfigError || // Direct flag set elsewhere
      error.includes('configurazione') || 
      error.includes('configura') || 
      error.includes('database') ||
      error.includes('folder ID') ||
      error.includes('caricato da Google Drive') ||
      error.includes('No active configuration');
    
    // If we have a 404 for documents, assume it's a configuration issue
    const isDocuments404 = window.location.pathname.includes('/documents') && 
                        document.querySelector('.status-code-404') !== null;
    
    if (isDocuments404 && !isConfigurationError) {
      console.log("Detected documents 404 but not marked as configuration error, overriding");
      return (
        <div className="text-center py-16 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 max-w-2xl mx-auto">
          <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20">
            <svg 
              className="h-16 w-16 text-blue-500"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
          </div>
          
          <h3 className="mt-4 text-lg font-medium text-blue-700 dark:text-blue-400">
            Configurazione Necessaria
          </h3>
          
          <p className="mt-2 text-md text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            Nessun documento caricato da Google Drive. Vai in Configurazioni e aggiungi il tuo folder ID.
          </p>
          
          <div className="mt-6">
            <a
              href="/configurazioni"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Vai alla pagina Configurazioni
            </a>
          </div>
        </div>
      );
    }
    
    const isAuthError = error.includes('login') || error.includes('sessione') || error.includes('autorizzata');
    
    console.log("Final error classification:", { isConfigurationError, isAuthError });
    
    return (
      <div className="text-center py-16 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 max-w-2xl mx-auto">
        {isConfigurationError ? (
          <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20">
            <svg 
              className="h-16 w-16 text-blue-500"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
          </div>
        ) :
          <svg 
            className="mx-auto h-16 w-16 text-red-500"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" 
            />
          </svg>
        }
        
        <h3 className={`mt-4 text-lg font-medium ${isConfigurationError ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {isConfigurationError 
            ? 'Configurazione Necessaria' 
            : isAuthError
              ? 'Accesso richiesto'
              : 'Errore durante il caricamento dei documenti'}
        </h3>
        
        {isConfigurationError ? (
          <>
            <p className="mt-2 text-md text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {isConfigError 
                ? 'Nessun documento caricato da Google Drive. Vai in Configurazioni e aggiungi il tuo folder ID.'
                : error}
            </p>
            <div className="mt-6">
              <a
                href="/configurazioni"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Vai alla pagina Configurazioni
              </a>
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">{error}</p>
        )}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-16 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700">
        <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-gray-50 dark:bg-gray-700">
          <svg 
            className="h-16 w-16 text-gray-400 dark:text-gray-500" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" 
            />
          </svg>
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">Cartella vuota</h3>
        <p className="mt-2 text-md text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          Non sono stati trovati documenti nella cartella Google Drive collegata. 
        </p>
        
        <div className="mt-4 px-4 py-3 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-md max-w-md mx-auto text-left text-sm">
          <p className="font-medium mb-2">Possibili soluzioni:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Verifica che la cartella Google Drive collegata contenga documenti</li>
            <li>Controlla che l'applicazione abbia accesso alla cartella Google Drive</li>
            <li>Se stai usando sottocartelle, assicurati che siano correttamente configurate</li>
            <li>Se hai appena aggiunto documenti, potrebbe essere necessario attendere qualche minuto per l'indicizzazione</li>
          </ol>
        </div>
        
        <div className="mt-6 space-y-3">
          <button 
            onClick={() => fetchDocuments(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Aggiorna e riprova
          </button>
          
          <Link
            to="/configurazioni"
            className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
            Verifica configurazione
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300">
            Documenti Google Drive <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({documents.length})</span>
          </h2>
          {configurations.length > 1 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Ricerca in {configurations.filter(c => c.is_active).length} cartelle: {configurations.filter(c => c.is_active).map(c => c.display_name).join(', ')}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-lg">
          <input
            type="text"
            placeholder="Cerca documenti o percorsi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-3 py-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {!searchTerm && <BreadcrumbNavigation />}

      {/* Folder and Document List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
        {searchTerm ? (
          // Search results view
          <div>
            <div className="bg-gray-50 dark:bg-gray-750 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Risultati della ricerca per: <span className="font-medium ml-1">{searchTerm}</span>
                <span className="ml-2 text-gray-500">({sortedDocuments.length} risultati)</span>
              </div>
            </div>
            
            {/* Table header */}
            <div className="hidden md:grid grid-cols-12 px-3 py-2 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">
              <div className="col-span-7">Nome</div>
              <div className="col-span-3 text-right">Ultima modifica</div>
              <div className="col-span-1 text-right">Dimensione</div>
              <div className="col-span-1"></div>
            </div>
            
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedDocuments.map((doc) => (
                <li key={doc.file_id}>
                  <DocumentItem document={doc} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          // Standard folder view
          <div>
            {/* Back button (only if we're in a subfolder) */}
            {currentFolder && (
              <div 
                className="flex items-center p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750"
                onClick={navigateUp}
              >
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Torna indietro</span>
                </div>
              </div>
            )}
            
            {/* Table header */}
            <div className="hidden md:grid grid-cols-12 px-3 py-2 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">
              <div className="col-span-7">Nome</div>
              <div className="col-span-3 text-right">Ultima modifica</div>
              <div className="col-span-1 text-right">Dimensione</div>
              <div className="col-span-1"></div>
            </div>
            
            {/* Folders */}
            {Array.from(currentFolderContents.subfolders).length > 0 && (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {Array.from(currentFolderContents.subfolders).map(folderPath => (
                  <FolderItem 
                    key={folderPath} 
                    folderPath={folderPath} 
                    onClick={() => navigateToFolder(folderPath)} 
                  />
                ))}
              </div>
            )}
            
            {/* Documents */}
            {sortedDocuments.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedDocuments.map((doc) => (
                  <li key={doc.file_id}>
                    <DocumentItem document={doc} />
                  </li>
                ))}
              </ul>
            ) : (
              // Empty folder message (only if no subfolders and no documents)
              Array.from(currentFolderContents.subfolders).length === 0 && (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">Cartella vuota</h3>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {documentPreview.isOpen && (
        <DocumentPreview
          fileId={documentPreview.fileId}
          fileName={documentPreview.fileName}
          contentType={documentPreview.contentType}
          onClose={closeDocumentPreview}
        />
      )}

      {/* Notification */}
      <Notification
        type={notification.type}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};

export default DocumentList; 